import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const STORAGE_KEY = "ih_cookie_consent";
const CURRENT_VERSION = "1.0";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom event so the Footer "Gestisci preferenze" button can reopen the banner
const REOPEN_EVENT = "ih:reopen-cookie-banner";

/**
 * Hook for cookie consent.
 * Returns: { status, openPreferences, accept, reject }
 *   - status: 'idle' | 'open' | 'accept_all' | 'necessary_only'
 *   - openPreferences(): force the banner to reappear
 */
export function useCookieConsent() {
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setStatus("open");
        return;
      }
      const stored = JSON.parse(raw);
      // If version mismatch, ask again
      if (stored.version !== CURRENT_VERSION) {
        setStatus("open");
        return;
      }
      setStatus(stored.choice);
    } catch {
      setStatus("open");
    }

    const reopen = () => setStatus("open");
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  const save = useCallback(async (choice) => {
    const payload = {
      version: CURRENT_VERSION,
      choice,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
    setStatus(choice);
    // Audit log to backend (non-blocking)
    try {
      await axios.post(`${API}/consents`, {
        version: CURRENT_VERSION,
        choice,
        page_url: window.location.pathname,
      });
    } catch {}
  }, []);

  return {
    status,
    accept: () => save("accept_all"),
    reject: () => save("necessary_only"),
    openPreferences: () => setStatus("open"),
  };
}

export function reopenCookieBanner() {
  window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
}
